// Initialize form
import * as Countries from "./countries";
import * as States from "./states";
import * as DynamicContentLoader from "./dynamicContentLoader";
import {
  initSignaturePad,
  getSignatureDataUrl,
  isSignatureEmpty,
  isSignatureValid,
} from "./signaturePadModule";


// Identify what each step contains without assuming fixed step numbers
function getStepType(stepNumber) {
  const stepEl = document.querySelector(`.step-${stepNumber}`);
  if (!stepEl) return null;
  if (stepEl.querySelector('#first_name')) return 'profile';
  if (stepEl.querySelector('#street') || stepEl.querySelector('#country')) return 'address';
  if (stepEl.querySelector('#new-password')) return 'password';
  if (stepEl.querySelector('#program_interest')) return 'credits';
  if (stepEl.querySelector('#acknowledge') || stepEl.querySelector('#signature-pad')) return 'enrollment';
  return null;
}

// Configuration
const stepCircles = document.querySelectorAll(".step-circle");
const config = {
  totalSteps: stepCircles.length || 4 // fallback to 4
};

let relationshipOptions = {};
let affiliations = [];
let programOptions = [];
let trackOptions = {};

// State management
const state = {
  currentStep: 1,
  guardian2Added: false
};

// ─────────────────────────────────────────────────────────────
// Password Step: dynamic, accessible validation w/ backend check
// ─────────────────────────────────────────────────────────────
let passwordState = {
  meetsLocal: false,
  meetsBackend: false,
  lastBackendValue: '',
  backendInFlight: false,
};

// DOM Elements
const elements = {
  progressBar: document.querySelector(".progress-bar-application"),
  studentNameSpan: document.getElementById("student_name_span"),
  formContainer: document.getElementById("form-container"),
  passwordInput: document.getElementById("password"),
  lengthReq: document.getElementById("lengthReq"),
  upperReq: document.getElementById("upperReq"),
  numberReq: document.getElementById("numberReq"),
  form: document.getElementById("multi-step-form"),
};

export async function initApplicationForm() {
  setupEventListeners();
  updateProgressBar();
  setDOBMax();
  setupCreditsStepBehavior();
  setupDragAndDropUpload();
  hideAllSteps();
  prefillFromURL();
  checkIfParentFormHasErrors();
  if (document.getElementById('new-password')) {
    initPasswordStep();
  }

  const dobInput = document.getElementById("date_of_birth");
  const guardian1Block = document.getElementById("guardian-section");
  const guardian2Toggle = document.getElementById("guardian2-toggle-container");
  const addGuardian2Btn = document.getElementById("add-guardian2-btn");
  const guardian2Block = document.getElementById("guardian2-section");
  const firstNameInput = document.getElementById("first_name");

  if (!dobInput || !guardian1Block || !guardian2Toggle || !addGuardian2Btn || !guardian2Block) {
    return;
  }

  if (firstNameInput) {
    firstNameInput.addEventListener("input", updateStudentNameDisplay);
  }

  if (document.querySelector("#student_phone_number")) {
    itiStudent = DynamicContentLoader.loadAndInitializeIntlTelApplicationInput("#student_phone_number");
  }
  if (document.querySelector("#guardian_phone_number")) {
    itiGuardian = DynamicContentLoader.loadAndInitializeIntlTelGuardianInput("#guardian_phone_number");
  }
  if (document.querySelector("#guardian2_phone_number")) {
    itiGuardian2 = DynamicContentLoader.loadAndInitializeIntlTelGuardian2Input("#guardian2_phone_number");
  }

  step1 = document.querySelector(".step-1");
  if (step1) {
    document.querySelector(".step-1").style.display = "block";
  }


  toggleGuardian(false); // hide guardian #1 by default
  toggleGuardian2(false); // hide guardian #2 by default

  // ── load your guardian/parent relationships from the server
  relationshipOptions = await fetchRelationshipOptions();
  populateRelationshipCategories();
  wireUpRelationshipHandlers();

  // whenever dob changes, show/hide guardian #1 and toggle button
  dobInput.addEventListener("change", e => {
    const age = calculateAge(new Date(e.target.value));
    const isMinor = age < 18;

    toggleGuardian(isMinor);
    guardian2Toggle.style.display = isMinor ? "block" : "none";

    // clear out any second-guardian state if no longer a minor
    if (!isMinor && state.guardian2Added) {
      toggleGuardian2(false);
    }
  });

  // click “Add another Parent/Guardian” to show/hide #2
  addGuardian2Btn.addEventListener("click", () => {
    toggleGuardian2(!state.guardian2Added);
  });

  function toggleGuardian(show) {
    guardian1Block.style.display = show ? "block" : "none";
    // update required attributes
    const fields = [
      "guardian_first_name",
      "guardian_last_name",
      "guardian_email",
      "guardian_phone_number",
      "guardian_rel_category",
      "guardian_relationship"
    ];
    fields.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      if (show) el.setAttribute("required", "");
      else     el.removeAttribute("required");
    });
  }

  function toggleGuardian2(show) {
    state.guardian2Added = show;
    guardian2Block.style.display = show ? "block" : "none";
    addGuardian2Btn.innerHTML  = show
      ? '<i class="fa-solid fa-user-minus me-1"></i> Remove Parent/Guardian'
      : '<i class="fa-solid fa-user-plus me-1"></i> Add another Parent/Guardian';

    // update required attributes
    const fields = [
      "guardian2_first_name",
      "guardian2_last_name",
      "guardian2_email",
      "guardian2_phone_number",
      "guardian2_rel_category",
      "guardian2_relationship"
    ];
    fields.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      if (show) el.setAttribute("required", "");
      else     el.removeAttribute("required");
    });
  }

  // populate country selects
  const countriesIds = [
    "country_of_birth",
    "country_of_citizenship",
    "country"
  ];
  Countries.populateCountrySelects(countriesIds);
  Countries.initCountrySelect2(countriesIds);

  // populate state selects
  const stateIds = [
    "state"
  ];
  await States.loadStates();
  States.populateStateSelects(stateIds);
  States.initStateSelect2(stateIds);
  setupCountryStateLinkage();
  await prefillAddressFromIP();

  affiliations = await fetchAffiliations();
  buildAffiliationMaps();
  populateProgramSelect();
  wireUpProgramTrackHandlers();
  initEnrollmentWhenVisible();
  initSubmitButton();
}

// Check parent form for errors
function checkIfParentFormHasErrors() {
  const parentEl = window.parent.document.getElementById('ApplicationInfo1_lbErr');
  if (!parentEl) return;
  if (parentEl) {
    const errEl = parentEl.querySelector('.errormsg');
    if (errEl) {
      const errorContainer = document.getElementById('errorContainer');
      const rowErrorContainer = document.getElementById('errorRow');
      if (errorContainer && rowErrorContainer) {
        errorContainer.innerHTML = errEl.innerHTML;
        rowErrorContainer.style.display = 'block';
      }
    }
  }
}

// Prefill Form
function prefillFromURL() {
  // pick the “right” query string (self vs parent)
  const search = (window.self !== window.top && window.parent.location)
    ? window.parent.location.search
    : window.location.search;

  const params = new URLSearchParams(search);
  for (let [key, value] of params.entries()) {
    const el = document.getElementById(key);
    if (!el) continue;

    // If this select is enhanced by Select2:
    if (window.$ && $(el).data('select2')) {
      $(el).val(value).trigger('change');
    } else {
      el.value = value;
      // notify any attached listeners
      el.dispatchEvent(new Event('input',  { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

// Event Listeners
export function setupEventListeners() {
  // Step navigation circles
  document.querySelectorAll(".step-circle").forEach((circle) => {
    circle.addEventListener("click", async function () {
      const targetStep = parseInt(this.getAttribute("data-step"), 10);

        // If moving forward, validate current step based on what it is
        if (targetStep > state.currentStep) {
          const currentType = getStepType(state.currentStep);
          let valid = true;
          switch (currentType) {
            case 'profile':
              valid = await validateProfileStep();
              break;
            case 'address':
              valid = validateAddress();
              break;
            case 'password':
              // let the password step’s own button guard progression;
              // but if you want to block circle jumping:
              valid = true; // or compute from passwordState if desired
              break;
            case 'credits':
              valid = validateCreditsStep();
              break;
            default:
              break;
          }
          if (!valid) return;
        }
        goToStep(targetStep);
      });
  });

  // Main form next step button
  document.querySelectorAll(".next-step").forEach((button) => {
    button.addEventListener("click", function () {
      const nextStep = parseInt(this.getAttribute("data-next"));
      handleNextStep(nextStep);
    });
  });

  // Assistance toggle
  needs_assistanceToggle = document.getElementById("needs_assistance");
  if (needs_assistanceToggle) {
    needs_assistanceToggle.addEventListener("change", function () {
      toggleAssistanceDetails(this.value === "yes");
    });
  }

  // Password validation
  if (elements.passwordInput) {
    elements.passwordInput.addEventListener("input", validatePassword);
  }
}

// Form Functions
export async function handleNextStep(nextStep) {
  // if going backwards, just jump
    if (nextStep < state.currentStep) {
    goToStep(nextStep);
    return;
  }

  const currentType = getStepType(state.currentStep);
  let valid = true;

  switch (currentType) {
    case 'profile':
      valid = await validateProfileStep();
      break;
    case 'address':
      valid = validateAddress();
      break;
    case 'password':
      valid = validatePasswordStep();
      break;
    case 'credits':
      valid = validateCreditsStep();
      break;
    default:
      valid = true;
  }

  if (!valid) {
    const form = document.getElementById("multi-step-form");
    form?.classList.add("was-validated");
    document.getElementById("form-container")
      ?.querySelector(".is-invalid")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }

  goToStep(nextStep);
}

export function validateCreditsStep() {
  const program = document.getElementById("program_interest").value;
  const track   = document.getElementById("track_interest").value;
  const transfer_credits     = document.getElementById("transfer_credits").value;
  const form = document.getElementById("multi-step-form");
  form.classList.add("was-validated");
  const fields = [
    "program_interest", "grade_level",
    "transfer_credits", "needs_assistance"
  ];

  let isValid = true;

  fields.forEach(id => {
    const el = document.getElementById(id);
    if (!el || !el.value.trim() || !el.value.trim().length) {
      isValid = false;
      el.classList.add("is-invalid");
    } else {
      el.classList.remove("is-invalid");
    }
  });
  // if they have credits, the file is required 
  const creditsFile = document.getElementById("transcript_file");

  if (creditsFile.required && transfer_credits === "yes" && (!creditsFile || !creditsFile.files.length)) {
    isValid = false;
  }

  // only enforce track if this program actually has multiple tracks
  const tracksForProgram = trackOptions[program] || [];
  const trackRequired    = tracksForProgram.length > 1;

  if (trackRequired) {
    if (!track) {
      isValid = false;
    }
  }

  return isValid;
}

function validateEnrollmentStep() {
  const signatureInput = document.getElementById("signature_data");
  const canvas         = document.getElementById("signature-pad");
  const form = document.getElementById("multi-step-form");
  const fields = [
    "class_start_date",
    "program_completion_date",
    "student_or_guardian_signature_name", // correct ID
    "signature_date"
  ];
  let valid = true;
  
  form.classList.add("was-validated");

  // 0) clear any previous validation errors
  form.querySelectorAll(".is-invalid").forEach(el => {
    el.classList.remove("is-invalid");
  });

  // 1) validate text/date inputs, including checkValidity for min/max on dates
  fields.forEach(id => {
    const el = document.getElementById(id);
    // if empty OR fails browser validity (e.g. date < min or > max)
    if (!el || !el.value.trim() || !el.checkValidity()) {
      valid = false;
      el.classList.add("is-invalid");
    } else {
      el.classList.remove("is-invalid");
    }
  });

  // 2) validate the checkbox properly
  const ack = document.getElementById("acknowledge");
  if (!ack.checked) {
    valid = false;
    ack.classList.add("is-invalid");
  } else {
    ack.classList.remove("is-invalid");
  }


  // 4) if there’s any invalid field, scroll the *first* one into view
  if (!valid) {
    const container = document.querySelector(".enrollment-agreement-container");
    // pick the first element with the Bootstrap “is-invalid” class
    const firstError = container.querySelector(".is-invalid");
    if (firstError) {
      firstError.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }

  if (isSignatureEmpty()) {
    // setting a custom error will make the form.invalid-feedback show
    signatureInput.classList.add("is-invalid");
    signatureInput.setCustomValidity("Please provide a valid signature.");
    // scroll to the canvas if all others are valid
    if (valid) {
      canvas.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
    valid = false;
  } else {
    const isValidSig = isSignatureValid({ threshold: 0.01});
    if (!isValidSig) {
      signatureInput.classList.add("is-invalid");
      signatureInput.setCustomValidity("Please provide a valid signature.");
      if (valid) {
        canvas.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
      valid = false;
    }
    else {
      signatureInput.value = getSignatureDataUrl();
      signatureInput.classList.remove("is-invalid");
    }
  }

  return valid;
}

export function validateAddress() {
  const fields = [
    "street", "state", "city", "country", "zip_code"
  ];
  let isValid = true;
    fields.forEach(id => {
    const el = document.getElementById(id);
    if (!el || !el.value.trim() || !el.value.trim().length) {
      isValid = false;
      el.classList.add("is-invalid");
    } else {
      el.classList.remove("is-invalid");
    }
  });

  const form = document.getElementById("multi-step-form");
  form.classList.add("was-validated");
  return isValid;
}

export async function validateProfileStep() {
  const dobEl = document.getElementById("date_of_birth");
  const dobVal = dobEl.value;
  const age    = calculateAge(new Date(dobVal));

  //  ── enforce minimum age of 12 ─────────────────────────────
  if (age < 12) {
    dobEl.setCustomValidity("Students must be at least 12 years old");
    dobEl.reportValidity();
    return false;
  } else {
    dobEl.setCustomValidity("");  // clear previous error
  }
  //  ────────────────────────────────────────────────────────────

  const isMinor = age < 18;
  let valid = true;
  const form = document.getElementById("multi-step-form");
  form.classList.add("was-validated");

  // always required
  const fields = [
    "first_name", "last_name",
    "gender",
    "student_email", "confirm_student_email",
    "student_phone_number",
    "country_of_birth", "date_of_birth", 
    "country_of_citizenship", "race_ethnicity"
  ];

    // only if minor
  if (isMinor) {
    fields.push(
      "guardian_first_name", "guardian_last_name",
      "guardian_email", "confirm_guardian_email",
      "guardian_phone_number",
      "guardian_rel_category", "guardian_relationship",
    );
    if (state.guardian2Added) {
      fields.push(
        "guardian2_first_name", "guardian2_last_name",
        "guardian2_email", "confirm_guardian2_email",
        "guardian2_phone_number",
        "guardian2_rel_category", "guardian2_relationship"
      );
    }
  }

  fields.forEach(id => {
    const el = document.getElementById(id);
    if (!el.checkValidity() || !el.value.trim() || !el.value.trim().length) {
      // add Bootstrap’s “is-invalid” class
      el.classList.add("is-invalid");
      valid = false;
    } else {
      // remove any previous error
      el.classList.remove("is-invalid");
    }
  });

  // 2) async-validate each email field
  const emailIds = ["student_email"];
  if (calculateAge(new Date(document.getElementById("date_of_birth").value)) < 18) {
    // minor → validate guardian(s)
    emailIds.push("guardian_email");
    if (state.guardian2Added) emailIds.push("guardian2_email");
  }

  for (let id of emailIds) {
    const ok = await validateEmail(id);
    if (!ok) valid = false;
  }

  // 3) validate phone numbers
  const phoneIds = [{instance: "itiApplication", selector: "student_phone_number"}];
  if (isMinor) {
    phoneIds.push({instance: "itiGuardian", selector: "guardian_phone_number"});
    if (state.guardian2Added) phoneIds.push({instance: "itiGuardian2", selector: "guardian2_phone_number"});
  }

  for (let id of phoneIds) {
    const ok = await DynamicContentLoader.isPhoneNumberValid(id.instance);
    if (!ok) {
      valid = false;
      const el = document.getElementById(id.selector);
      // 2) clear any previous custom errors
      if (el) {
        el.setCustomValidity("");
        el.setCustomValidity("We couldn’t verify that phone number.");
        el.reportValidity();
        el.classList.add("is-invalid");
      }
    } else {
      const el = document.getElementById(id.selector);
      if (el) {
        el.setCustomValidity("");
        el.classList.remove("is-invalid");
      }
    }
  }

  return valid;
}

function getEnrollmentStepEl() {
  // any step that contains the signature canvas OR the acknowledge checkbox
  const steps = Array.from(document.querySelectorAll('.step'));
  return steps.find(s => s.querySelector('#signature-pad') || s.querySelector('#acknowledge')) || null;
}

// Password Validation
export function validatePasswordStep() {
  const pw  = document.getElementById('new-password');
  const pw2 = document.getElementById('confirm-new-password');
  const backendFeedbackElement = document.getElementById('set-password-backend-feedback');
  const form = document.getElementById('multi-step-form');

  // If this template has no password step, don't block navigation.
  if (!pw || !pw2) return true;

  // Helpers (scoped here so they don't leak globals)
  const setIcon = (id, ok) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.toggle('fa-check-circle', ok);
    el.classList.toggle('fa-times-circle', !ok);
    el.style.color = ok ? '#78981fFF' : '#ef3a437f';
  };

  const updateBackendFeedback = (message) => {
    if (!backendFeedbackElement) return;
    backendFeedbackElement.innerHTML = '';
    if (!message || !message.feedback) return;
    const { warning, suggestions } = message.feedback;
    if (warning) backendFeedbackElement.innerHTML += `<small>${warning}</small><br>`;
    if (Array.isArray(suggestions)) {
      suggestions.forEach(s => backendFeedbackElement.innerHTML += `<small>${s}</small><br>`);
    }
  };

  const runBackendValidation = async (password) => {
    // debounce/in-flight guard
    if (passwordState.backendInFlight) return;

    passwordState.backendInFlight = true;
    if (backendFeedbackElement) {
      backendFeedbackElement.innerHTML = `<small>Checking password strength…</small>`;
    }
    setIcon('new-backend', false);

    try {
      const endpointUrl = '/api/method/zoni_edu.zoni_edu.doctype.account_activation.account_activation.validate_password_strength';
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Frappe-CSRF-Token': frappe.csrf_token
        },
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      const message = data?.message || {};
      passwordState.meetsBackend = !!message.meets_requirements;
      passwordState.lastBackendValue = password;
      updateBackendFeedback(message);
      setIcon('new-backend', passwordState.meetsBackend);
    } catch (err) {
      console.error('Error validating password:', err);
      passwordState.meetsBackend = false;
      if (backendFeedbackElement) {
        backendFeedbackElement.innerHTML = `<small>Could not verify password right now. Please try again.</small>`;
      }
      setIcon('new-backend', false);
    } finally {
      passwordState.backendInFlight = false;
    }
  };

  // 1) LOCAL CHECKS
  const v1 = pw.value || '';
  const v2 = pw2.value || '';

  const hasLen = v1.length >= 8 && v1.length <= 64;
  const hasL   = /[a-z]/.test(v1);
  const hasU   = /[A-Z]/.test(v1);
  const hasN   = /[0-9]/.test(v1);
  const hasS   = /[^\w\s]/.test(v1);       // symbol
  const match  = v1 === v2 && v2.length > 0;

  setIcon('new-8char', hasLen);
  setIcon('new-lcase', hasL);
  setIcon('new-ucase', hasU);
  setIcon('new-num',   hasN);
  setIcon('new-symbol',hasS);
  setIcon('new-pwmatch', match);

  passwordState.meetsLocal = hasLen && hasL && hasU && hasN && hasS && match;

  // Clear previous invalids and then mark what failed
  [pw, pw2].forEach(el => el.classList.remove('is-invalid'));

  if (!passwordState.meetsLocal) {
    if (!(hasLen && hasL && hasU && hasN && hasS)) pw.classList.add('is-invalid');
    if (!match) pw2.classList.add('is-invalid');
    form?.classList.add('was-validated');
    // Focus first offending field
    const firstBad = pw.classList.contains('is-invalid') ? pw : (pw2.classList.contains('is-invalid') ? pw2 : null);
    if (firstBad) firstBad.focus();
    return false;
  }

  // 2) BACKEND CHECK GATING (no button disabling)
  const needsBackendCheck =
    !passwordState.meetsBackend || passwordState.lastBackendValue !== v1;

  if (needsBackendCheck) {
    // Fire-and-forget backend check; user can click Continue again after it completes.
    runBackendValidation(v1);
    // Block navigation until we have a positive backend result.
    return false;
  }

  // All good — locals + backend already OK for this value
  return true;
}

export function updateRequirement(element, isValid) {
  if (isValid) {
    element.classList.add("valid");
    element.querySelector(".icon").textContent = "✓";
  } else {
    element.classList.remove("valid");
    element.querySelector(".icon").textContent = "❌";
  }
}

export function isValidPassword(password) {
  return (
    password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)
  );
}

// Navigation Functions

export function goToStep(stepNumber) {
  const prev = document.querySelector(`.step-${state.currentStep}`);
  const next = document.querySelector(`.step-${stepNumber}`);
  prev.style.display = "none";
  next.style.display = "block";
  state.currentStep = stepNumber;
  updateProgressBar();
  document.getElementById("multi-step-form").classList.remove("was-validated");

  const formContainer = document.getElementById("form-container");
  formContainer.scrollTop = 0;
  window.scrollTo({ top: formContainer.offsetTop - 20, behavior: 'smooth' });

  const type = getStepType(stepNumber);
  if (type !== 'enrollment') {
    const firstField = next.querySelector("input, select, textarea");
    if (firstField) firstField.focus();
  } else {
    // we just entered the enrollment step; if the canvas is visible, init now
    setTimeout(() => initEnrollmentWhenVisible(), 0);
  }
}

// Helper Functions
async function fetchCountryName(code) {
  const res = await fetch(`/api/method/zoni_edu.services.get_country_name?code=${code}`);
  const { message } = await res.json();
  return message;
}

async function fetchAffiliations() {
  const res = await fetch('/api/method/zoni_edu.services.zahs_affiliations');
  const { message } = await res.json();
  return message;
}


function buildAffiliationMaps() {
  // reset
  programOptions = [];
  trackOptions   = {};

  // 1) pick out each “top-level” program row
  //    (where text === program)
  const programIdByName = {};
  affiliations.forEach(a => {
    if (a.text === a.program) {
      programOptions.push({ value: a.value, text: a.text });
      trackOptions[a.value] = [];
      programIdByName[a.program] = a.value;
    }
  });

  // 2) assign all the actual tracks into the right bucket
  affiliations.forEach(a => {
    // skip the top-level rows
    if (a.text !== a.program) {
      const pid = programIdByName[a.program];
      if (pid) {
        trackOptions[pid].push({
          value: a.value,
          text:  a.text
        });
      }
    }
  });
}


function populateProgramSelect() {
  const sel = document.getElementById('program_interest');
  if (!sel) return;

  sel.innerHTML = [
    '<option value="">Select</option>',
    ...programOptions.map(p =>
      `<option value="${p.value}">${p.text}</option>`
    )
  ].join('');
}

function wireUpProgramTrackHandlers() {
  const progSel  = document.getElementById('program_interest');
  const trackSel = document.getElementById('track_interest');
  if (!progSel || !trackSel) return;

  progSel.addEventListener('change', () => {
    const pid    = progSel.value;
    const tracks = trackOptions[pid] || [];

    trackSel.innerHTML = [
      '<option value="">Select</option>',
      ...tracks.map(t =>
        `<option value="${t.value}">${t.text}</option>`
      )
    ].join('');

    // disable if no tracks for this program
    trackSel.disabled = tracks.length === 0;
  });

  // trigger initial fill (in case you pre-select)
  progSel.dispatchEvent(new Event('change'));
}

async function fetchRelationshipOptions() {
  const res = await fetch('/api/method/zoni_edu.services.zahs_guardian_parent_relationships');
  const { message } = await res.json();
  return message.reduce((acc, { value, text, category }) => {
    if (!acc[category]) acc[category] = [];
    acc[category].push({ value, text });
    return acc;
  }, {});
}

function populateRelationshipCategories() {
  const categories = Object.keys(relationshipOptions);
  ['guardian_rel_category', 'guardian2_rel_category'].forEach(selectId => {
    const sel = document.getElementById(selectId);
    if (!sel) return;
    // render “Select” + one <option> per category
    sel.innerHTML = [
      '<option value="">Select</option>',
      ...categories.map(cat =>
        `<option value="${cat}">` +
          `${cat[0].toUpperCase() + cat.slice(1)}` + 
        `</option>`
      )
    ].join('');
  });
}

function wireUpRelationshipHandlers() {
  const pairs = [
    ['guardian_rel_category', 'guardian_relationship'],
    ['guardian2_rel_category','guardian2_relationship']
  ];

  pairs.forEach(([catId, relId]) => {
    const cat = document.getElementById(catId);
    const rel = document.getElementById(relId);
    if (!cat || !rel) return;

    cat.addEventListener('change', () => {
      const opts = relationshipOptions[cat.value] || [];
      rel.innerHTML = '<option value="">Select</option>';
      opts.forEach(({ value, text }) => {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = text;
        rel.appendChild(o);
      });
      rel.disabled = opts.length === 0;
    });
  });
}

function setDOBMax() {
  const dob = document.getElementById("date_of_birth");
  if (!dob) return;

  const today = new Date();
  // subtract 12 years
  const year  = today.getFullYear() - 12;
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day   = String(today.getDate()).padStart(2, "0");

  dob.max = `${year}-${month}-${day}`; 
}

export async function validateEmail(id) {
  const el = document.getElementById(id);
  // 1) run your local-regex check first
  const pattern = /^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i;
  if (!pattern.test(el.value.trim())) {
    //el.setCustomValidity("Please enter a valid email address.");
    el.reportValidity();
    return false;
  }
  // 2) clear any previous custom error so the backend can run
  el.setCustomValidity("");
  // 3) your existing backend check
  const ok = await validateEmailOnBackend(id);
  if (!ok) {
    el.setCustomValidity("We couldn’t verify that address.");
    el.reportValidity();
    return false;
  }
  // 4) all good
  el.setCustomValidity("");
  return true;
}


async function validateEmailOnBackend(emailID) {
  const el = document.getElementById(emailID);
  if (el && el.value.trim()) {
    try {
      const response = await fetch(
        `/api/method/zoni_edu.services.validate_email?email=${encodeURIComponent(el.value.trim())}`,
        { headers: { "Content-Type": "application/json" } }
      );
      const { message } = await response.json();
      if (message === 0) {
        //document.getElementById("multi-step-form").classList.remove("was-validated");
        el.classList.add("is-invalid");
        el.classList.remove("is-valid");
        console.error("Email validation failed:", message);
        return false;
      } else {
        el.classList.remove("is-invalid");
        return true;
      }
    } catch (error) {
      console.error("Error validating email:", error);
      el.classList.add("is-invalid");
      console.error("Email validation failed due to network error:", error);
      return false;
    }
  }
  return false;
}

export function calculateAge(birthDate) {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

export function updateStudentNameDisplay() {
  const firstName = document.getElementById("first_name").value;
  elements.studentNameSpan.textContent = firstName;
}

export function hideAllSteps() {
  document.querySelectorAll(".step-1, .step-2, .step-3, .step-4, .step-5").forEach((el) => {
    el.style.display = "none";
  });
}

export function updateProgressBar() {
  if (!elements.progressBar) return;
  const progressPercentage =
    ((state.currentStep - 1) / (config.totalSteps - 1)) * 100;
  elements.progressBar.style.width = progressPercentage + "%";
}

// Component Initializers
export function setupCreditsStepBehavior() {
  const transfer_creditsSelect = document.getElementById("transfer_credits");
  const uploadContainer  = document.getElementById("creditsUploadContainer");
  const programSelect    = document.getElementById("program_interest");
  const trackContainer   = document.getElementById("track-interest-container");
  const trackSelect      = document.getElementById("track_interest");

  if (!transfer_creditsSelect || !uploadContainer || !programSelect || !trackContainer || !trackSelect) return;

  programSelect.addEventListener("change", function () {
    const pid    = this.value;
    const tracks = trackOptions[pid] || [];
    const show   = tracks.length > 1;

    trackContainer.style.display = show ? "block" : "none";
    if (show) {
      trackSelect.setAttribute("required", "");
    } else {
      trackSelect.removeAttribute("required");
      trackSelect.value = "";
    }
  });

  // on init, in case there's a pre-selected program
  programSelect.dispatchEvent(new Event('change'));

  transfer_creditsSelect.addEventListener("change", function () {
    const showUpload = this.value === "yes";
    uploadContainer.style.visibility = showUpload ? "visible" : "hidden";
    uploadContainer.style.height     = showUpload ? "auto" : "0";
    uploadContainer.style.overflow   = showUpload ? "visible" : "hidden";
  });
}

export function setupDragAndDropUpload() {
  const dropArea = document.getElementById("drop-area");
  const fileInput = document.getElementById("transcript_file");
  const feedback = document.getElementById("upload-feedback");

  if (!dropArea || !fileInput || !feedback) return;

  ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
    dropArea.addEventListener(eventName, (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
  });

  dropArea.addEventListener("dragover", () => {
    dropArea.classList.add("border-primary", "bg-light");
  });

  dropArea.addEventListener("dragleave", () => {
    dropArea.classList.remove("border-primary", "bg-light");
  });

  dropArea.addEventListener("drop", (e) => {
    dropArea.classList.remove("border-primary", "bg-light");
    const files = e.dataTransfer.files;
    handleFileUpload(files, fileInput, feedback);
  });

  fileInput.addEventListener("change", () => {
    handleFileUpload(fileInput.files, fileInput, feedback);
  });
}

export function handleFileUpload(files, fileInput, feedback) {
  if (files.length > 0) {
    fileInput.files = files;
    feedback.style.display = "block";
    feedback.textContent = `Selected: ${files[0].name}`;
  }
}

export function toggleAssistanceDetails(show) {
  document.getElementById("assistanceDetails").style.display = show
    ? "block"
    : "none";
}
export async function collectFormData() {
  const formData = {
    profile: {
      first_name: document.getElementById("first_name").value,
      last_name: document.getElementById("last_name").value,
      middle_name: document.getElementById("middle_name").value,
      gender: document.getElementById("gender").value,
      student_email: document.getElementById("student_email").value,
      confirm_student_email: document.getElementById("confirm_student_email").checked,
      student_phone_number: DynamicContentLoader.getPhoneNumber('itiApplication'),
      country_of_birth: await fetchCountryName(document.getElementById("country_of_birth").value),
      date_of_birth: document.getElementById("date_of_birth").value,
      country_of_citizenship: await fetchCountryName(document.getElementById("country_of_citizenship").value),
      race_ethnicity: document.getElementById("race_ethnicity").value,
    },
    guardian: {
      first_name: document.getElementById("guardian_first_name").value,
      last_name: document.getElementById("guardian_last_name").value,
      email: document.getElementById("guardian_email").value,
      confirm_guardian_email: document.getElementById("confirm_guardian_email").checked,
      phone_number: DynamicContentLoader.getPhoneNumber('itiGuardian'),
      relationship_category: document.getElementById("guardian_rel_category").value,
      relationship: document.getElementById("guardian_relationship").value,
    },
    guardian2: {
      first_name: document.getElementById("guardian2_first_name").value,
      last_name: document.getElementById("guardian2_last_name").value,
      email: document.getElementById("guardian2_email").value,
      confirm_guardian2_email: document.getElementById("confirm_guardian2_email").checked,
      phone_number: DynamicContentLoader.getPhoneNumber('itiGuardian2'),
      relationship_category: document.getElementById("guardian2_rel_category").value,
      relationship: document.getElementById("guardian2_relationship").value,
    },
    address: {
      street: document.getElementById("street").value,
      city: document.getElementById("city").value,
      state: document.getElementById("state").value,
      country: await fetchCountryName(document.getElementById("country").value),
      zip_code: document.getElementById("zip_code").value,
    },
    password: {
      password: document.getElementById("new-password")?.value || "",
      confirm_password: document.getElementById("confirm-new-password")?.value || ""
    },

    credits: {
      program_interest: document.getElementById("program_interest").value,
      track_interest: document.getElementById("track_interest").value,
      grade_level: document.getElementById("grade_level").value,
      transfer_credits: document.getElementById("transfer_credits").value,
      transcript_file: document.getElementById("transcript_file").files[0]?.name || null,
      needs_assistance: document.getElementById("needs_assistance").value,
    },

    enrollment: {
      class_start_date: document.getElementById("class_start_date").value,
      program_completion_date: document.getElementById("program_completion_date").value,
      acknowledge: document.getElementById("acknowledge").checked,
      student_or_guardian_signature_name: document.getElementById("student_or_guardian_signature_name").value,
      signature_date: document.getElementById("signature_date").value,
    }
  };

  return formData;
}

export function showSuccessMessage() {
  // Create a success modal or message

  document.getElementById("enrollment-success").style.display = "block"; // Show success
  document.getElementById("progress-div").style.display = "none"; // Hide progress bar
}

export async function submitFormData(formData) {
  const submitBtn = document.getElementById("submit-enroll-btn");
  submitBtn.disabled = true;
  submitBtn.innerHTML =
    '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Submitting...';

  // build the payload
  const doc = {
    doctype: "ZAHS Application",
    first_name:                   formData.profile.first_name,
    last_name:                    formData.profile.last_name,
    middle_name:                  formData.profile.middle_name,
    gender:                       formData.profile.gender,
    student_email:                formData.profile.student_email,
    confirm_student_email:        formData.profile.confirm_student_email ? 1 : 0,
    student_phone_number:         formData.profile.student_phone_number,
    country_of_birth:             formData.profile.country_of_birth,
    date_of_birth:                formData.profile.date_of_birth,
    country_of_citizenship:       formData.profile.country_of_citizenship,
    race_ethnicity:               formData.profile.race_ethnicity,

    guardian_first_name:          formData.guardian.first_name,
    guardian_last_name:           formData.guardian.last_name,
    guardian_email:               formData.guardian.email,
    confirm_guardian_email:       formData.guardian.confirm_guardian_email ? 1 : 0,
    guardian_phone_number:        formData.guardian.phone_number,
    guardian_relationship:        formData.guardian.relationship,

    guardian2_first_name:         formData.guardian2.first_name,
    guardian2_last_name:          formData.guardian2.last_name,
    guardian2_email:              formData.guardian2.email,
    confirm_guardian2_email:      formData.guardian2.confirm_guardian2_email ? 1 : 0,
    guardian2_phone_number:       formData.guardian2.phone_number,
    guardian2_relationship:       formData.guardian2.relationship,

    street:                       formData.address.street,
    city:                         formData.address.city,
    state:                        formData.address.state,
    country:                      formData.address.country,
    zip_code:                     formData.address.zip_code,

    password:                     formData.password.password,
    confirm_password:             formData.password.confirm_password,

    affiliation:                  formData.credits.track_interest || formData.credits.program_interest,
    grade_level:                  formData.credits.grade_level,
    transfer_credits:             formData.credits.transfer_credits === "yes" ? "Yes" : "No",
    //transcript:                   formData.credits.transcript_file,
    need_assistance:              formData.credits.needs_assistance === "yes" ? "Yes" : "No",

    class_start_date:             formData.enrollment.class_start_date,
    program_completion_date:      formData.enrollment.program_completion_date,
    student_or_guardian_signature_name: formData.enrollment.student_or_guardian_signature_name,
    acknowledge:                  formData.enrollment.acknowledge ? 1 : 0,
    signature_date:               formData.enrollment.signature_date,
  };

  // Adjust date_of_birth format to MM/DD/YYYY
  const [year, month, day] = formData.profile.date_of_birth.split("-");
  const formattedDob = `${month}/${day}/${year}`;

  const vals = {
    first_name:                   doc.first_name,
    last_name:                    doc.last_name,
    middle_name:                  doc.middle_name,
    gender:                       doc.gender,
    student_email:                doc.student_email,
    student_phone_number:         doc.student_phone_number,
    date_of_birth:                formattedDob,            // in MM/DD/YYYY
    country_of_birth:             document.getElementById("country_of_birth").value,
    country_of_citizenship:       document.getElementById("country_of_citizenship").value,
    race_ethnicity:               doc.race_ethnicity,
    guardian_first_name:          doc.guardian_first_name || doc.first_name,
    guardian_last_name:           doc.guardian_last_name || doc.last_name,
    guardian_email:               doc.guardian_email || doc.student_email,
    guardian_phone_number:        doc.guardian_phone_number || doc.student_phone_number,
    guardian_relationship:        doc.guardian_relationship || "9998",
    guardian2_first_name:         doc.guardian2_first_name,
    guardian2_last_name:          doc.guardian2_last_name,
    guardian2_email:              doc.guardian2_email,
    guardian2_phone_number:       doc.guardian2_phone_number,
    guardian2_relationship:       doc.guardian2_relationship,
    street:                       doc.street,
    city:                         doc.city,
    state:                        doc.state,
    country:                      document.getElementById("country").value,
    zip_code:                     doc.zip_code,
    affiliation:                  doc.affiliation,
    grade_level:                  doc.grade_level,
  };

  let res, payload;
  try {
    res = await fetch('/api/resource/ZAHS Application', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Frappe-CSRF-Token': frappe.csrf_token
      },
      body: JSON.stringify(doc)
    });
    payload = await res.json();

    if (res.ok) {
      // Fill and send the values to the parent window
      const docname = payload.data.name;

      const sigDataUrl = document.getElementById("signature_data").value;
      if (sigDataUrl) {
        // Data URL → Blob
        const blob = await (await fetch(sigDataUrl)).blob();
        const sigForm = new FormData();
        sigForm.append("file", blob, "signature.png");
        sigForm.append("doctype", "ZAHS Application");
        sigForm.append("docname", docname);
        sigForm.append("fieldname", "signature_data");
        sigForm.append("is_private", 1);
    
        try {
          const sigRes = await fetch("/api/method/upload_file", {
            method: "POST",
            headers: { "X-Frappe-CSRF-Token": frappe.csrf_token },
            body: sigForm
          });
          const sigPayload = await sigRes.json();
          if (sigRes.ok) {
            const sigUrl = sigPayload.message.file_url;
            // link it into the doc’s Attach field
            try {
              const updateRes = await fetch(`/api/resource/ZAHS Application/${docname}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Frappe-CSRF-Token': frappe.csrf_token
                },
                body: JSON.stringify({ signature_data: sigUrl })
              });
              if (!updateRes.ok) {
                console.error('Failed to link signature:', await updateRes.json());
              }
            } catch (updateErr) {
              console.error('Error linking signature:', updateErr);
            }            
          } else {
            console.error("Signature upload failed:", await sigRes.text());
          }
        } catch (e) {
          console.error("Network error during signature upload:", e);
        }
      }


      const fileInput = document.getElementById("transcript_file");
      if (fileInput.files.length) {
        const transcript = fileInput.files[0];
        const fileForm = new FormData();
        fileForm.append('file', transcript);
        fileForm.append('doctype', 'ZAHS Application');
        fileForm.append('docname', docname);
        fileForm.append('is_private', 1);
        // IMPORTANT: use the exact fieldname of your Attach field
        fileForm.append('fieldname', 'transcript_file');

        try {
          const fileRes = await fetch('/api/method/upload_file', {
            method: 'POST',
            headers: {
              'X-Frappe-CSRF-Token': frappe.csrf_token
            },
            body: fileForm
          });
          const filePayload = await fileRes.json();
          if (fileRes.ok) {
            const fileUrl = filePayload.message.file_url;
            // link it into the doc’s Attach field
            try {
              const updateRes = await fetch(`/api/resource/ZAHS Application/${docname}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Frappe-CSRF-Token': frappe.csrf_token
                },
                body: JSON.stringify({ transcript_file: fileUrl })
              });
              if (!updateRes.ok) {
                console.error('Failed to link transcript file:', await updateRes.json());
              }
            } catch (updateErr) {
              console.error('Error linking transcript file:', updateErr);
            }
          } else {
            console.error('File upload failed:', filePayload);
          }
        } catch (err) {
          console.error('Network error during file upload:', err);
        }
      }

      parent.postMessage({ action: 'setAllFields', values: vals }, window.origin);

      function submitParentForm() {
        const btn = parent.document.getElementById('ApplicationInfo1_btnSave');
        if (btn) btn.click();
        else console.warn('Could not find ApplicationInfo1_btnSave in parent');
      }
      setTimeout(submitParentForm, 100);
      //showSuccessMessage();
    } else {
      console.error(payload);
      showAlert(payload.message || 'Submission failed.');
    }
  } catch (err) {
    console.error(err);
  } finally {
    // Clear UI fields after we’ve kicked off the parent submit/redirect
    const pw  = document.getElementById("new-password");
    const pw2 = document.getElementById("confirm-new-password");
    if (pw)  pw.value  = "";
    if (pw2) pw2.value = "";
    // Keep your existing spinner/redirect text
    //submitBtn.disabled = false;
    submitBtn.innerHTML =
    '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Redirecting to payment...';
  }
}

function setupCountryStateLinkage() {
  const $country = $('#country');
  const $state   = $('#state');
  if (!$country.length || !$state.length) return;

  let ignoreLinkage = false;

  const countryHandler = () => {
    if (ignoreLinkage) return;
    ignoreLinkage = true;
    if ($country.val() !== 'US') {
      // force “Other” on the state select without looping back
      $state.val('00').trigger('change');
    }
    ignoreLinkage = false;
  };

  const stateHandler = () => {
    if (ignoreLinkage) return;
    ignoreLinkage = true;
    if ($state.val() === '00') {
      // force “Other” on the country select without looping back
      $country.val('Other').trigger('change');
    } else {
      // force “US” on the country select without looping back
      $country.val('US').trigger('change');
    }
    ignoreLinkage = false;
  };

  // catch both the native change and Select2’s own select event
  $country.on('change', countryHandler);
  $country.on('select2:select', countryHandler);

  $state.on('change', stateHandler);
  $state.on('select2:select', stateHandler);
}

function initEnrollmentWhenVisible() {
  const stepEl = getEnrollmentStepEl();
  if (!stepEl) return;

  // Only init when the canvas is actually laid out (non-zero size)
  const tryInit = () => {
    const canvas = stepEl.querySelector('#signature-pad');
    if (!canvas) return false;
    const visible = stepEl.style.display !== 'none' && canvas.offsetWidth > 0 && canvas.offsetHeight > 0;
    if (!visible) return false;

    // safe to initialize now
    initSignaturePad();
    setDatesToToday();
    setProgramCompletionDateRange();
    updateSuggestedCompletionDate();
    updateSignatureName();
    return true;
  };

  // 1) try immediately (in case we entered directly)
  if (tryInit()) return;

  // 2) watch for the step becoming visible (style/class changes)
  const mo = new MutationObserver(() => {
    if (stepEl.style.display === 'block') {
      // give layout a tick
      setTimeout(() => {
        if (tryInit()) mo.disconnect();
      }, 0);
    }
  });
  mo.observe(stepEl, { attributes: true, attributeFilter: ['style', 'class'] });

  // 3) also listen to window resizes (mobile keyboards, rotations, etc.)
  const onResize = () => {
    if (tryInit()) window.removeEventListener('resize', onResize);
  };
  window.addEventListener('resize', onResize);
}

function initSubmitButton() {
  const submitBtn = document.getElementById("submit-enroll-btn");
  const multiStepForm = document.getElementById("multi-step-form");
  const signatureInput = document.getElementById("signature_data");
  if (!multiStepForm || !signatureInput || !submitBtn) return;
  submitBtn.addEventListener("click", async () => {
    if (submitBtn.disabled) return;              // guard
    submitBtn.disabled = true;                   // disable immediately
    multiStepForm.classList.add("was-validated");
    if (!validateEnrollmentStep()) {
      submitBtn.disabled = false;                  // re-enable on failure
      return;
    }
    const formData = await collectFormData();
    formData.signature = signatureInput.value;
    await submitFormData(formData);
  });
}

function setDatesToToday() {
  const today = new Date().toISOString().split('T')[0];
  ['class_start_date', 'signature_date'].forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.value = today;
    }
  });
}

function setProgramCompletionDateRange() {
  const el = document.getElementById("program_completion_date");
  if (!el) return;

  const today = new Date();

  // min = 30 days from now
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + 30);

  // max = 6 years from now
  const maxDate = new Date(today);
  maxDate.setFullYear(maxDate.getFullYear() + 6);

  const fmt = d => d.toISOString().split("T")[0];

  el.min = fmt(minDate);
  el.max = fmt(maxDate);
}

function updateSuggestedCompletionDate() {
  const grade = parseInt(document.getElementById('grade_level').value, 10);
  const el    = document.getElementById('program_completion_date');
  if (!el || isNaN(grade)) return;

  // today
  const today = new Date();

  // mapping grades → suggested years
  const map = {
    9:  2.5,
    10: 2.0,
    11: 1.5,
    12: 1.0,
  };
  let years = map[grade] ?? 2.5;

  // build the suggested date
  const suggested = new Date(today);
  suggested.setMonth(suggested.getMonth() + Math.round(years * 12));

  // clamp to your existing min/max
  const min = new Date(today);    min.setDate(min.getDate() + 30);
  const max = new Date(today);    max.setFullYear(max.getFullYear() + 6);

  if (suggested < min) suggested.setTime(min.getTime());
  if (suggested > max) suggested.setTime(max.getTime());

  el.value = suggested.toISOString().slice(0,10);
}

function updateSignatureName() {
  const dobVal = document.getElementById('date_of_birth').value;
  if (!dobVal) return;
  const age = calculateAge(new Date(dobVal));

  let fullName = '';
  if (age < 18) {
    // minor → use guardian
    const g1 = document.getElementById('guardian_first_name').value.trim();
    const g2 = document.getElementById('guardian_last_name').value.trim();
    fullName = [g1, g2].filter(Boolean).join(' ');
  } else {
    // adult → use student
    const f = document.getElementById('first_name').value.trim();
    const m = document.getElementById('middle_name').value.trim();
    const l = document.getElementById('last_name').value.trim();
    fullName = [f, m, l].filter(Boolean).join(' ');
  }

  const sig = document.getElementById('student_or_guardian_signature_name');
  if (sig) sig.value = fullName;
}

/**
 * Fetch the user’s location and prefill country, state, city & zip.
 */
async function prefillAddressFromIP() {
  try {
    let endpoint = '/api/method/zoni_edu.services.loc';
    if (window.location.host === 'highschool.zoni.edu') {
      endpoint = 'https://www.zoni.edu' + endpoint;
    }
    const res = await fetch(endpoint);
    const { message } = await res.json();
    if (!message) return;
    if (message.message === "Unknown") return;

    // 1) City & ZIP (plain inputs)
    const cityEl = document.getElementById('city');
    const zipEl  = document.getElementById('zip_code');
    if (cityEl) cityEl.value = message.city_name || '';
    if (zipEl)  zipEl.value  = message.postal_code || '';

    // 2) Country (Select2)
    //    we assume country <select> has id="country"
    if (window.$ && $('#country').length) {
      $('#country')
        .val(message.country_iso_code)      // e.g. "US"
        .trigger('change.select2');         // notify Select2
    } else {
      const c = document.getElementById('country');
      if (c) c.value = message.country_iso_code;
    }

    // 3) State (Select2)
    //    make sure States.STATES is already loaded when you call this
    if (message.country_iso_code === 'US') {
      if (window.$ && $('#state').length) {
        $('#state')
          .val(message.subdivision_1_iso_code) // e.g. "NJ"
          .trigger('change.select2');
      } else {
        const s = document.getElementById('state');
        if (s) s.value = message.subdivision_1_iso_code;
      }
    } else {
      if (window.$ && $('#state').length) {
        $('#state')
          .val('00')
          .trigger('change.select2');
      } else {
        const s = document.getElementById('state');
        if (s) s.value = '00';
      }
    }
  } catch (err) {
    console.error('Could not prefill address from IP:', err);
  }
}


function initPasswordStep() {
  const pw = document.getElementById('new-password');
  const pw2 = document.getElementById('confirm-new-password');
  const toggle1 = document.getElementById('new-password-toggle');
  const toggle2 = document.getElementById('confirm-new-password-toggle');

  // Scope the "Continue" button inside the password step (works in both HTMLs)
  const stepNum = [...document.querySelectorAll('[class^="step step-"]')]
    .map(el => el.className.match(/step-(\d+)/)?.[1])
    .filter(Boolean)
    .map(Number)
    .find(n => document.querySelector(`.step-${n} #new-password`));
  const pwStepEl  = stepNum ? document.querySelector(`.step-${stepNum}`) : null;
  const nextBtn   = pwStepEl ? pwStepEl.querySelector('.next-step') : null;

  // Backend feedback container + per-rule icons
  const backendFeedbackElement = document.getElementById('set-password-backend-feedback');
  const ruleIds = {
    len:  'new-8char',
    l:    'new-lcase',
    u:    'new-ucase',
    n:    'new-num',
    s:    'new-symbol',
    eq:   'new-pwmatch'
  };

  if (!pw || !pw2 || !backendFeedbackElement || !nextBtn || !toggle1 || !toggle2) return;

  // toggle visibility buttons
  const toggleVisibility = (input, btn) => {
    const type = input.getAttribute('type') === 'password' ? 'text' : 'password';
    input.setAttribute('type', type);
    btn.innerHTML = type === 'password'
      ? '<i class="fas fa-eye-slash" aria-hidden="true"></i>'
      : '<i class="fas fa-eye" aria-hidden="true"></i>';
  };
  toggle1.addEventListener('click', () => toggleVisibility(pw, toggle1));
  toggle2.addEventListener('click', () => toggleVisibility(pw2, toggle2));

  // icon color/shape update
  const setIcon = (id, ok) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.toggle('fa-check-circle', ok);
    el.classList.toggle('fa-times-circle', !ok);
    el.style.color = ok ? '#78981fFF' : '#ef3a437f';
  };

  // debounce helper
  let debounceTimer;
  const debounce = (fn, ms = 300) => (...args) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => fn(...args), ms);
  };

  // local checks (fast)
  const runLocalChecks = () => {
    const v1 = pw.value || '';
    const v2 = pw2.value || '';
    const hasLen = v1.length >= 8 && v1.length <= 64;
    const hasL   = /[a-z]/.test(v1);
    const hasU   = /[A-Z]/.test(v1);
    const hasN   = /[0-9]/.test(v1);
    const hasS   = /[^\w\s]/.test(v1); // symbol
    const match  = v1 === v2 && v2.length > 0;

    setIcon(ruleIds.len, hasLen);
    setIcon(ruleIds.l,   hasL);
    setIcon(ruleIds.u,   hasU);
    setIcon(ruleIds.n,   hasN);
    setIcon(ruleIds.s,   hasS);
    setIcon(ruleIds.eq,  match);

    passwordState.meetsLocal = hasLen && hasL && hasU && hasN && hasS && match;
  };

  // backend check (only if local checks pass)
  const validatePasswordBackend = async (password) => {
    const endpointUrl = '/api/method/zoni_edu.zoni_edu.doctype.account_activation.account_activation.validate_password_strength';
    try {
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Frappe-CSRF-Token': frappe.csrf_token
        },
        body: JSON.stringify({ password })
      });
      return await response.json();
    } catch (err) {
      console.error('Error validating password:', err);
      return { message: { meets_requirements: false, feedback: {} } };
    }
  };

  const updateBackendFeedback = (message) => {
    backendFeedbackElement.innerHTML = '';
    if (!message || !message.feedback) return;
    const { warning, suggestions } = message.feedback;
    if (warning) backendFeedbackElement.innerHTML += `<small>${warning}</small><br>`;
    if (Array.isArray(suggestions)) {
      suggestions.forEach(s => backendFeedbackElement.innerHTML += `<small>${s}</small><br>`);
    }
  };

  const runAllChecks = async () => {
    runLocalChecks();

    // backend only after locals pass & value changed
    const v1 = pw.value || '';
    if (!passwordState.meetsLocal) {
      passwordState.meetsBackend = false;
      setIcon('new-backend', false);
      updateBackendFeedback({ feedback: {} });
      return;
    }

    passwordState.backendInFlight = true;
    const result = await validatePasswordBackend(v1);
    console.log('Backend password validation result:', result);
    passwordState.backendInFlight = false;
    passwordState.lastBackendValue = v1;

    const ok = !!result?.message?.meets_requirements;
    console.log('Backend password meets requirements:', ok);
    passwordState.meetsBackend = ok;
    setIcon('new-backend', ok);                // ← update the icon here
    updateBackendFeedback(result?.message || { feedback: {} });
  };

  const runAllChecksDebounced = debounce(runAllChecks, 250);

  pw.addEventListener('input', runAllChecksDebounced);
  pw2.addEventListener('input', runAllChecksDebounced);

  // initial paint
  runAllChecks();
}